export const DASHBOARD_TYPE_URL_PREFIX_MAP = {
  custom: '/dashboards',
  journal: '/dashboards/journal',
  institution: '/dashboards/funding-institution',
  affiliation: '/dashboards/institution'
}

export const getSearchLink = ({ slug, dashboardType, doisUpdatedTS }) => {
  const keyMap = {
    custom: 'dashboardSlug',
    affiliation: 'affiliations[0]',
    journal: 'journals[0]'
  }
  const key = keyMap[dashboardType]
  if (!key) {
    return null
  }

  const value = dashboardType === 'journal' ? slug.split('-').slice(0, -1).join(' ') : slug
  let link = `/search?${key}=${value}`
  if (dashboardType === 'custom') {
    link += `&dashboardDOIsUpdatedTs=${doisUpdatedTS}`
  }
  return link
}

const count = n => n.toLocaleString('en-US')

/**
 * Builds the post-save message for "add search results to an existing collection".
 *
 * Two things can surprise the user, and the API reports neither in a form we can
 * just print (SCI-3863):
 *
 *   - Papers already in the collection are skipped, so the number added is lower
 *     than the number matched. The API does count these for us.
 *   - A single add resolves at most `resultsLimit` results and drops the rest
 *     SILENTLY. Unlike the create-from-search response, AddDoisToDashboardRS has
 *     no `warning` field to carry that, so we derive it here from the result count
 *     the search page already knows. Without this an Enterprise user adding a
 *     45,000-result search sees "Added 10000 papers" and no hint that 35,000 were
 *     dropped.
 *
 * Returns null when there is nothing worth flagging, which tells apiCallWithToasts
 * to show the ordinary success toast instead of the yellow warning one.
 */
export const buildAddSearchResultsMessage = ({
  addedCount = 0,
  alreadyPresentCount = 0,
  collectionName = '',
  resultCount,
  resultsLimit
} = {}) => {
  const truncated = typeof resultCount === 'number' && typeof resultsLimit === 'number' && resultCount > resultsLimit

  const note = truncated
    ? `Your search returned ${count(resultCount)} results, but only the first ${count(resultsLimit)} could be added. `
    : ''

  if (addedCount === 0) {
    return `${note}All matching papers are already in ${collectionName}.`
  }

  const added = `Added ${count(addedCount)} ${addedCount === 1 ? 'paper' : 'papers'} to ${collectionName}.`

  if (alreadyPresentCount > 0) {
    const already = alreadyPresentCount === 1 ? 'was' : 'were'
    return `${note}${added} ${count(alreadyPresentCount)} ${already} already in the collection.`
  }

  return note ? `${note}${added}` : null
}
